// Report a bug, with a picture, and have it survive the click.
//
// A screenshot is required. "The X is broken" costs a round trip every time and
// the reporter has usually navigated away before anyone can ask, so the report
// is now defined to include the image — which inverts the old write order. The
// object goes to Storage first and the row second: a row claiming a screenshot
// that never uploaded is worse than no row, because triage would trust it.
//
// Three ways in, one handler: paste (Win+Shift+S then Ctrl+V is the shortest
// path from seeing the bug to attaching it), a file picker (the discoverable,
// keyboard- and screen-reader-reachable one, and the only one that works on
// mobile), and screen capture. All of them end at accept().
//
// It is the shared Dialog now (SH-07). Hand-built, it let Tab walk out into the
// page behind it, and one stray click on the backdrop, or Escape, threw away the
// typed report and its screenshot without asking.
import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { Badge, Button } from './primitives';
import { Eyebrow } from './card';
import { Dialog } from './dialog';
import { Field, TextArea } from './field';
import { InlineStatus } from './feedback';
import { supabase } from '../lib/supabase';

const MAX_BODY = 4000;
// Matches the bucket's server-side file_size_limit (20260806100000). The bucket
// is the real control; this only saves a doomed round trip.
const MAX_BYTES = 2 * 1024 * 1024;
const MAX_EDGE = 1600;
const BUCKET = 'bug-screenshots';

// jsdom implements neither of these, and both are optional to the flow, so both
// degrade rather than throw — which is what keeps the submit path testable.
const objectUrl = (b: Blob) => {
  try {
    return URL.createObjectURL(b);
  } catch {
    return '';
  }
};

/**
 * Downscale and re-encode to WebP with the native canvas — no dependency, and
 * it takes a 4 MB full-screen PNG snip to roughly 150 kB.
 *
 * The re-encode is also a security control: whatever went in, what comes out is
 * raster pixels. An SVG or an HTML file renamed to .png cannot survive
 * drawImage. On any failure (no canvas, no createImageBitmap, a decode error)
 * the original blob passes through and the bucket's allowed_mime_types is what
 * rejects it.
 */
async function shrink(src: Blob): Promise<Blob> {
  try {
    const bmp = await createImageBitmap(src);
    const scale = Math.min(1, MAX_EDGE / Math.max(bmp.width, bmp.height));
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(bmp.width * scale);
    canvas.height = Math.round(bmp.height * scale);
    const ctx = canvas.getContext('2d');
    if (!ctx) return src;
    ctx.drawImage(bmp, 0, 0, canvas.width, canvas.height);
    const out = await new Promise<Blob | null>((r) => canvas.toBlob(r, 'image/webp', 0.8));
    return out ?? src;
  } catch {
    return src;
  }
}

export function BugReportModal({
  screen,
  screenLabel,
  onClose,
}: {
  screen: string;
  screenLabel: string;
  onClose: () => void;
}) {
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filedId, setFiledId] = useState<string | null>(null);
  const [shot, setShot] = useState<{ blob: Blob; url: string } | null>(null);
  // Screen capture would otherwise photograph this dialog instead of the bug.
  const [hidden, setHidden] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const shotLabel = useId();

  const accept = useCallback(async (f: Blob | null | undefined) => {
    if (!f) return;
    // Advisory only — the bucket's allowed_mime_types is what actually enforces
    // this, because a File's type comes from the OS and can lie.
    if (!f.type.startsWith('image/') || f.type === 'image/svg+xml') {
      setError('That isn’t an image. PNG, JPEG or WebP.');
      return;
    }
    const small = await shrink(f);
    if (small.size > MAX_BYTES) {
      setError('That screenshot is too big even after resizing — try capturing a smaller area.');
      return;
    }
    setError(null);
    setShot((prev) => {
      if (prev?.url) URL.revokeObjectURL(prev.url);
      return { blob: small, url: objectUrl(small) };
    });
  }, []);

  const clearShot = () =>
    setShot((prev) => {
      if (prev?.url) URL.revokeObjectURL(prev.url);
      return null;
    });

  // Paste anywhere in the dialog, not just the textarea — people paste at the
  // window and expect it to land.
  useEffect(() => {
    const onPaste = (e: ClipboardEvent) => {
      const item = Array.from(e.clipboardData?.items ?? []).find((i) =>
        i.type.startsWith('image/'),
      );
      if (!item) return;
      e.preventDefault();
      void accept(item.getAsFile());
    };
    window.addEventListener('paste', onPaste);
    return () => window.removeEventListener('paste', onPaste);
  }, [accept]);

  // Release the preview blob if the dialog closes with one attached.
  useEffect(
    () => () => {
      if (shot?.url) URL.revokeObjectURL(shot.url);
    },
    [shot],
  );

  const capture = async () => {
    setError(null);
    setHidden(true);
    // Two frames: one to apply visibility, one for the browser to actually paint
    // without the dialog. One is not enough.
    await new Promise<void>((r) => requestAnimationFrame(() => requestAnimationFrame(() => r())));
    let stream: MediaStream | null = null;
    try {
      stream = await navigator.mediaDevices.getDisplayMedia({ video: true, audio: false });
      const video = document.createElement('video');
      video.srcObject = stream;
      await video.play();
      await new Promise<void>((r) => requestAnimationFrame(() => r()));
      const canvas = document.createElement('canvas');
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      canvas.getContext('2d')?.drawImage(video, 0, 0);
      const blob = await new Promise<Blob | null>((r) => canvas.toBlob(r, 'image/webp', 0.9));
      await accept(blob);
    } catch {
      // Denied, cancelled, blocked by policy, or unavailable (Wayland, insecure
      // context, no mediaDevices at all). Every one of those lands here, and the
      // other two ways in still work — so say so rather than leaving a dead button.
      setError('Couldn’t capture the screen — paste an image or choose a file instead.');
    } finally {
      stream?.getTracks().forEach((t) => t.stop());
      setHidden(false);
    }
  };

  const submit = async () => {
    if (busy || !body.trim() || !shot) return;
    if (!supabase) {
      setError('Not connected.');
      return;
    }
    setBusy(true);
    setError(null);

    // getSession is local — no network round trip — and the uid is needed before
    // the upload because it IS the folder the storage policy checks.
    const { data: sess } = await supabase.auth.getSession();
    const uid = sess.session?.user.id;
    if (!uid) {
      setBusy(false);
      setError('Your session expired — sign in again and the report will still be here.');
      return;
    }

    const ext =
      shot.blob.type === 'image/png' ? 'png' : shot.blob.type === 'image/jpeg' ? 'jpg' : 'webp';
    // The reporter's filename is never used: the path is uid/uuid.ext, which is
    // what makes traversal unexpressible and the key unguessable. The uid prefix
    // is the folder the bug_shot_insert policy matches against auth.uid().
    const path = `${uid}/${crypto.randomUUID()}.${ext}`;
    const { error: upErr } = await supabase.storage
      .from(BUCKET)
      .upload(path, shot.blob, { contentType: shot.blob.type, upsert: false });
    if (upErr) {
      setBusy(false);
      setError(`Couldn’t upload the screenshot — ${upErr.message}`);
      return;
    }

    // `reporter` and `created_at` are deliberately absent: both are stamped by
    // the database, and the INSERT grant does not even include them.
    const { data, error: insErr } = await supabase
      .from('bug_reports')
      .insert({
        screen,
        body: body.trim().slice(0, MAX_BODY),
        user_agent: navigator.userAgent.slice(0, 400),
        screenshot_path: path,
      })
      .select('id')
      .single();

    setBusy(false);
    // The report is the point. On failure keep everything they typed and the
    // attachment — the object is already up, so a retry just orphans one blob.
    if (insErr) {
      setError(insErr.message);
      return;
    }
    setFiledId(data.id as string);

    // Fire and forget, but READ the result: functions.invoke never rejects — it
    // resolves with {data, error} — so a .catch() here would be dead code. The
    // email leg also reports its own misconfiguration with a 200, which is how
    // it stayed silently dead for two releases; surface that to the console.
    const { data: fn, error: fnErr } = await supabase.functions.invoke('bug-report', {
      body: { id: data.id },
    });
    if (fnErr) console.warn('bug-report email failed', fnErr);
    else if ((fn as { emailConfigured?: boolean } | null)?.emailConfigured === false)
      console.warn('bug-report: RESEND_API_KEY / BUG_REPORT_TO unset — no email was sent');
  };

  const incomplete = !body.trim() || !shot;
  return (
    <Dialog
      // Closed, not hidden, for the capture: a modal <dialog> and its backdrop
      // sit in the top layer, above everything the capture is meant to show.
      // What was typed and attached lives in this component, so it is all
      // still there when the dialog comes back.
      open={!hidden}
      title="Report a bug"
      onClose={onClose}
      // A report in progress is not lost to a stray click or Escape: the Dialog
      // asks before it discards.
      dirty={!filedId && (body.trim() !== '' || shot !== null)}
      footer={
        filedId ? (
          <Button variant="primary" onClick={onClose}>
            Done
          </Button>
        ) : (
          // No Cancel beside it: it would sit 8px from Send, a slip away from
          // the primary action. The ×, Escape and the backdrop all close, and
          // all three ask "Discard changes?" first once anything is typed.
          <Button
            variant="primary"
            icon="bug"
            loading={busy}
            disabled={incomplete}
            onClick={() => void submit()}
          >
            Send report
          </Button>
        )
      }
    >
      {filedId ? (
        <p
          role="status"
          style={{
            margin: 0,
            fontSize: 'var(--hf-text-md)',
            lineHeight: 'var(--hf-leading-body)',
            color: 'var(--hf-ink-2)',
          }}
        >
          Thanks — report{' '}
          <span style={{ fontFamily: 'var(--hf-font-mono)' }}>#{filedId.slice(0, 8)}</span> is filed
          and the team has been notified.
        </p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--hf-space-4)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--hf-space-2)' }}>
            <Eyebrow>Screen</Eyebrow>
            <Badge>{screenLabel}</Badge>
          </div>

          <Field
            label="What happened?"
            hint="What you did, what you expected, and what happened instead."
            required
          >
            <TextArea
              rows={5}
              maxLength={MAX_BODY}
              value={body}
              onChange={(v) => {
                setBody(v);
                if (error) setError(null);
              }}
            />
          </Field>

          <div
            role="group"
            aria-labelledby={shotLabel}
            style={{ display: 'flex', flexDirection: 'column', gap: 'var(--hf-space-2)' }}
          >
            <Eyebrow id={shotLabel}>Screenshot, required</Eyebrow>
            {shot ? (
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--hf-space-3)' }}>
                <img
                  src={shot.url}
                  alt="Attached screenshot"
                  style={{
                    maxWidth: 200,
                    maxHeight: 120,
                    borderRadius: 'var(--hf-radius-md)',
                    border: '1px solid var(--hf-rule-2)',
                  }}
                />
                <Button size="sm" onClick={clearShot}>
                  Remove
                </Button>
              </div>
            ) : (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--hf-space-2)',
                  flexWrap: 'wrap',
                }}
              >
                {/* Secondary, not ghost: as borderless text these read as
                    labels, and attaching is a required step. */}
                <Button icon="file" onClick={() => fileRef.current?.click()}>
                  Choose a file
                </Button>
                <Button onClick={() => void capture()}>Capture screen</Button>
                <span style={{ fontSize: 'var(--hf-text-sm)', color: 'var(--hf-ink-3)' }}>
                  or press Ctrl+V to paste one
                </span>
              </div>
            )}
            <input
              ref={fileRef}
              type="file"
              accept="image/png,image/jpeg,image/webp"
              aria-label="Attach a screenshot"
              style={{ display: 'none' }}
              onChange={(e) => {
                void accept(e.target.files?.[0]);
                e.target.value = '';
              }}
            />
            <p style={{ margin: 0, fontSize: 'var(--hf-text-xs)', color: 'var(--hf-ink-3)' }}>
              Screenshots are visible to owners and admins, and are deleted when the report is
              marked done.
            </p>
          </div>

          {/* In the dialog, not a toast: everything outside an open modal is
              inert, the toast host included. */}
          {error ? (
            <InlineStatus tone="error">{error}</InlineStatus>
          ) : (
            // Why Send is unavailable, said beside it rather than left to guess.
            incomplete && (
              <p style={{ margin: 0, fontSize: 'var(--hf-text-xs)', color: 'var(--hf-ink-3)' }}>
                Add a description and a screenshot to send.
              </p>
            )
          )}
        </div>
      )}
    </Dialog>
  );
}
